<script setup>
const props = defineProps({
  blok: Array,
  actions: Object,
})

const emits = defineEmits(['closeSideMenu', 'open', 'close', 'hoverMenuLv2', 'hoverMenuLv1'])
const activeIndex = ref(null)
const isActive = ref(null)

function openMenuLevel(index) {
  activeIndex.value = index
  isActive.value = true
}

function closeAllMenu() {
  activeIndex.value = null
  isActive.value = false
}

function handleClose() {
  document.body.style.overflow = 'auto'
  emits('closeSideMenu')
  closeAllMenu()
}
</script>

<template>
  <div class="mobile">
    <div class="fixed inset-0 z-[-1] overflow-hidden bg-[#0e0c1f]/50 backdrop-blur-[3px]" aria-hidden="true" data-headlessui-state="open" @click="handleClose" />
    <div class="max-w-full min-h-full w-[min(22rem)] bg-white shadow-custom-lg ring-1 ring-secondary-gray-200">
      <nav class="relative h-full text-base">
        <div class="nav-header flex items-center bg-white px-4 py-2 lg:h-auto">
          <div class="ml-auto cursor-pointer">
            <svg v-if="activeIndex !== null" class="text-primary/80" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 512 512" @click="closeAllMenu"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="48" d="M244 400L100 256l144-144M120 256h292" /></svg>
            <svg v-else class="text-primary/80" xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" @click="handleClose"><path fill="currentColor" d="m12 13.4l-4.9 4.9q-.275.275-.7.275t-.7-.275q-.275-.275-.275-.7t.275-.7l4.9-4.9l-4.9-4.9q-.275-.275-.275-.7t.275-.7q.275-.275.7-.275t.7.275l4.9 4.9l4.9-4.9q.275-.275.7-.275t.7.275q.275.275.275.7t-.275.7L13.4 12l4.9 4.9q.275.275.275.7t-.275.7q-.275.275-.7.275t-.7-.275L12 13.4Z" /></svg>
          </div>
        </div>
        <div
          class="submenu h-full max-h-full px-4 pb-10"
        >
          <div v-for="(item, index) in blok" :key="item._uid" class="relative flex flex-col text-xs xs:text-sm">
            <div v-if="!isActive" class="py-2.5">
              <a
                v-if="item.target" :href="item.link" target="_blank" rel="noopener noreferrer"
              ><span class="text-primary font-medium hover:text-secondary-red-100">{{ item.label }}</span></a>
              <div
                v-else
                class="navigation flex items-center"
              >
                <NuxtLink class="w-auto whitespace-nowrap" :to="localePath({ path: item.link ? item.link : '' })" @click="handleClose">
                  <span class="text-primary font-medium hover:text-secondary-red-100">{{ item.label }}</span>
                </NuxtLink>
                <div v-if="item.submenu?.length > 0" class="ml-auto w-full flex cursor-pointer justify-end text-primary hover:text-secondary-red-100" @click="openMenuLevel(index)">
                  <svg
                    class="ml-10 -rotate-90" height="18" width="18" viewBox="0 0 24 24"
                  ><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m7 10l5 5l5-5" /></svg>
                </div>
              </div>
            </div>
            <div v-if="item.submenu?.length >= 0 && activeIndex === index" class="mobile-submenu py-2.5 pb-4">
              <div
                v-for="(submenu_lv1, index_lv1) in item.submenu"
                v-if="item.submenu?.length" :key="submenu_lv1._uid"
                class="mb-0"
              >
                <div class="flex items-center py-2">
                  <NuxtLink class="flex" :to="localePath({ path: submenu_lv1.link })" @click="handleClose">
                    <span class="text-xs text-primary font-semibold tracking-tight xs:text-sm hover:text-secondary-red-100">
                      {{ submenu_lv1.label }}
                    </span>
                  </NuxtLink>
                </div>

                <ul class="mobile-submenu-inner grid border-l-2 border-secondary-gray-200 pl-2">
                  <li v-for="(submenu_lv2, index_lv2) in submenu_lv1.submenu" :key="submenu_lv2._uid" class="m-0" :class="submenu_lv2.label ? 'mb-3 last:mb-0' : ''">
                    <div
                      v-if="submenu_lv2.label"
                      class="flex items-center justify-between"
                      @click="submenu_lv2.submenu?.length ? submenu_lv2.active = !submenu_lv2.active : false"
                    >
                      <NuxtLink
                        class="flex items-start justify-start"
                        :to="localePath({ path: submenu_lv2.link ? submenu_lv2.link : null })"
                        :class="submenu_lv2.submenu?.length ? 'w-[100%]' : 'w-full'"
                        @click="handleClose"
                      >
                        <span class="font-medium leading-6 hover:text-secondary-red-100">{{ submenu_lv2.label }}</span>
                      </NuxtLink>
                      <div v-if="submenu_lv2.submenu?.length" class="group w-1/2 flex cursor-pointer justify-end pl-3 text-primary hover:text-secondary-red-100">
                        <svg :class="submenu_lv2.active ? '-rotate-180' : 'rotate-0 '" class="ml-10 transition-transform duration-200 ease-in" height="18" width="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="m7 10l5 5l5-5" /></svg>
                      </div>
                    </div>
                    <ul
                      v-if="submenu_lv2.active || submenu_lv2.submenu?.length && !submenu_lv2.label"
                      class="mobile-submenu-deep p-0"
                    >
                      <li v-for="(submenu_lv3, index_lv3) in submenu_lv2.submenu" :key="submenu_lv3._uid" class="grid m-0">
                        <NuxtLink
                          class="flex items-center py-2"
                          :class="submenu_lv3.query ? '[&>*]:!text-primary [&>*]:hover:!text-secondary-red-100' : ''"
                          :to="localePath({ path: submenu_lv3.link ? submenu_lv3.link : '', query: { value: submenu_lv3.query ? submenu_lv3.label : '' } })"
                          @click="handleClose"
                        >
                          <div class="mr-1">
                            <svg class="text-primary/70" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 15 15"><path fill="currentColor" fill-rule="evenodd" d="M5 7.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 1-.5-.5Z" clip-rule="evenodd" /></svg>
                          </div>
                          <span class="font-medium leading-5 hover:text-secondary-red-100">{{ submenu_lv3.label }}</span>
                        </NuxtLink>
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <ul v-if="actions" class="absolute bottom-0 w-full inline-flex flex-wrap items-center justify-center gap-2 bg-white p-3">
          <li v-for="action in actions" :key="action.uuid" class="mb-0 flex-auto">
            <StoryblokComponent :theme="action.theme" :size="action.size" :blok="action" @on-click="handleClose" />
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>
